import { TeamBand } from "@/components/TeamBand";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShoppingBag, Download } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Stars } from "@/components/Stars";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

const FILTERS = [
  { key: "all", label: "Tout" },
  { key: "ergonomie", label: "Ergonomie" },
  { key: "epi", label: "EPI" },
];

export default function Catalogue() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sub, setSub] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("default");
  const { add } = useCart();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const byCat = filter === "all" ? products : products.filter((p) => p.category === filter);
  const subs = [...new Set(byCat.map((p) => p.subcategory).filter(Boolean))].sort();
  let shown = byCat.filter((p) => sub === "all" || p.subcategory === sub);
  if (q.trim()) {
    const t = q.toLowerCase();
    shown = shown.filter((p) => p.name.toLowerCase().includes(t) || (p.short || "").toLowerCase().includes(t));
  }
  if (sort !== "default") {
    shown = [...shown].sort((a, b) => {
      const pa = a.price || 0, pb = b.price || 0;
      if (pa === 0 && pb === 0) return 0;
      if (pa === 0) return 1;
      if (pb === 0) return -1;
      return sort === "price-asc" ? pa - pb : pb - pa;
    });
  }

  return (
    <div data-testid="catalogue-page" className="pt-[148px]">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Boutique</p></Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none">Catalogue</h1>
          <p className="mt-6 text-lg max-w-xl" style={{ color: "var(--ink-2)" }}>
            Matériel ergonomique et équipements de protection individuelle sélectionnés pour leur qualité et leur conformité.
          </p>
        </Reveal>

        <div className="mt-12 flex flex-wrap gap-3">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => { setFilter(f.key); setSub("all"); }} data-testid={`filter-${f.key}`}
              className="px-6 py-3 font-medium text-sm transition-colors duration-200"
              style={{
                border: "1px solid var(--ink)",
                background: filter === f.key ? "var(--ink)" : "transparent",
                color: filter === f.key ? "var(--bone)" : "var(--ink)",
              }}>
              {f.label}
            </button>
          ))}
        </div>

        {subs.length > 1 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="subcategory-filters">
            <button onClick={() => setSub("all")} data-testid="subfilter-all"
              className="px-4 py-2 text-xs font-semibold transition-colors duration-200"
              style={{ border: "1px solid var(--border)", background: sub === "all" ? "var(--orange)" : "transparent", color: sub === "all" ? "var(--bone)" : "var(--ink-2)" }}>
              Toutes ({byCat.length})
            </button>
            {subs.map((s) => {
              const count = byCat.filter((p) => p.subcategory === s).length;
              return (
                <button key={s} onClick={() => setSub(s)} data-testid={`subfilter-${s}`}
                  className="px-4 py-2 text-xs font-semibold transition-colors duration-200"
                  style={{ border: "1px solid var(--border)", background: sub === s ? "var(--orange)" : "transparent", color: sub === s ? "var(--bone)" : "var(--ink-2)" }}>
                  {s} ({count})
                </button>
              );
            })}
            {filter === "epi" && (
              <Link to="/comparateur-ethylotests" className="px-4 py-2 text-xs font-bold ml-auto self-center link-hover" style={{ color: "var(--orange)" }} data-testid="compare-link">
                Comparer les éthylotests →
              </Link>
            )}
            {filter === "ergonomie" && (
              <Link to="/comparateur-exosquelettes" className="px-4 py-2 text-xs font-bold ml-auto self-center link-hover" style={{ color: "var(--orange)" }} data-testid="compare-exo-link">
                Comparer les exosquelettes →
              </Link>
            )}
          </div>
        )}
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="relative w-full md:max-w-sm">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--ink-2)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un produit…" data-testid="catalogue-search"
              className="field" style={{ paddingLeft: "2.5rem" }} />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm whitespace-nowrap" style={{ color: "var(--ink-2)" }}>{shown.length} produit{shown.length > 1 ? "s" : ""}</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="catalogue-sort" className="field" style={{ width: "auto" }}>
              <option value="default">Trier : pertinence</option>
              <option value="price-asc">Prix croissant</option>
              <option value="price-desc">Prix décroissant</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
          {shown.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08}>
              <div className="group p-6 h-full flex flex-col" style={{ background: "var(--white)" }} data-testid={`product-card-${p.id}`}>
                <Link to={`/catalogue/${p.id}`} className="overflow-hidden mb-6 relative block" data-testid={`product-link-${p.id}`}>
                  {p.badge && (
                    <span className="absolute top-3 left-3 z-10 px-3 py-1 overline" style={{ background: "var(--orange)", color: "var(--bone)" }}>{p.badge}</span>
                  )}
                  <img src={p.image} alt={p.name} loading="lazy" className="w-full h-60 object-cover transition-transform duration-700 group-hover:scale-105" />
                </Link>
                <span className="overline mb-2" style={{ color: "var(--ink-2)" }}>{p.category === "epi" ? "EPI" : "Ergonomie"}{p.stock_qty === 0 ? " · Rupture" : p.stock === "sur_commande" ? " · Sur commande" : ""}</span>
                <Link to={`/catalogue/${p.id}`}><h3 className="font-display font-bold text-xl mb-2 link-hover inline-block">{p.name}</h3></Link>
                <p className="text-sm flex-1" style={{ color: "var(--ink-2)" }}>{p.short}</p>
                {p.rating_count > 0 && (
                  <div className="flex items-center gap-1 mt-3" data-testid={`card-rating-${p.id}`}>
                    <Stars value={p.rating_avg} size={13} />
                    <span className="text-xs" style={{ color: "var(--ink-2)" }}>({p.rating_count})</span>
                  </div>
                )}
                <div className="mt-6 flex items-center justify-between gap-2">
                  <span className="font-display font-black text-xl" style={{ color: "var(--ink)" }}>Sur devis</span>
                  <div className="flex items-center gap-2">
                    <Link to={`/contact?produit=${encodeURIComponent(p.name)}`} data-testid={`product-devis-${p.id}`}
                      className="btn-primary px-4 py-2.5 text-sm font-semibold">Demander un devis</Link>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        {shown.length === 0 && <p className="py-20 text-center" style={{ color: "var(--ink-2)" }}>Aucun produit dans cette catégorie.</p>}
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/products/catalogue/pdf`} target="_blank" rel="noopener noreferrer"
          className="btn-outline mt-10 inline-flex items-center gap-2 px-6 py-3 font-semibold" data-testid="download-products-pdf-btn">
          <Download size={16} /> Télécharger le catalogue produits (PDF)
        </a>
      </section>
      <TeamBand />
    </div>
  );
}
